import React, { useRef, useState } from 'react';
import { Button, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { cleanTableName, tableNameError } from './tableList.utils';
import tableNameErrorText from './tableNameErrorText';

/**
 * Add a table by name (7, 1A, Terrace 2): Enter or Add table adds it at the end of the list. An
 * empty name or one the restaurant already has is refused under the field; the text stays and the
 * error goes when typing.
 */
export default function AddTableForm({ tables, onAdd }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Tables:TableList' });
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const submit = async (event) => {
    event.preventDefault();

    if (saving) {
      return;
    }

    const problem = tableNameErrorText(t, tableNameError(value, tables));

    if (problem) {
      setError(problem);

      return;
    }

    setSaving(true);
    const serverError = await onAdd(cleanTableName(value));
    setSaving(false);

    if (serverError) {
      setError(serverError);

      return;
    }

    setValue('');
    setError(null);
    inputRef.current?.focus();
  };

  return (
    <form onSubmit={submit} noValidate className="max-w-[560px]" data-testid="table-add-form">
      <label htmlFor="table-ordering-add-name" className="block mb-2xs">
        {t('add.label')}
      </label>
      <div className="flex flex-wrap gap-xs">
        <Input
          id="table-ordering-add-name"
          ref={inputRef}
          className="min-w-[200px] flex-1"
          value={value}
          placeholder={t('add.placeholder')}
          autoComplete="off"
          status={error ? 'error' : undefined}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? 'table-ordering-add-error' : undefined}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
        />
        <Button htmlType="submit" icon={<PlusOutlined />} loading={saving}>
          {t('add.button')}
        </Button>
      </div>
      {error && (
        <div id="table-ordering-add-error" role="alert" className="mt-2xs text-sm text-brand-danger">
          {error}
        </div>
      )}
    </form>
  );
}
